import _ from 'lodash'
import { pointers } from '@wix/thunderbolt-becky-types'
import { getContextFromInflatedId } from '../../../byRefUtils'
import {
	EMPTY_REMOVED,
	LAYOUT_INFLATED_MAP,
	PRESET_RESOLVING_DATA_ITEM_TYPE,
	RESOLVED_REFS_NAMESPACE,
	SITE_NAMESPACE,
} from '../../../constants'
import { getDataItemRefsWithOverrides, resolveOverrideItem } from '../../dataConsumption'
import { getInflatedDataItemsMap } from '../../presetsResolving'
import type { DataRefsForPresetResolving } from '../../types'
import type { DependenciesData } from './types'
import { createConsumptionDataNode } from './utils/dataNodeUtils'

export const LayoutData = createConsumptionDataNode<
	DependenciesData,
	DataRefsForPresetResolving,
	DataRefsForPresetResolving
>({
	getDependencies: ({
		queryName,
		templateComponent,
		getOverrideDataRefs,
		createRefInTemplateNamespace,
		getLeafsOverridesMapsRefs,
		getParentRefCompResolvedPresets,
	}) => {
		const refArraysWithOverrides = getDataItemRefsWithOverrides({
			templateComponent,
			queryName,
			createRefInTemplateNamespace,
			getOverrideDataRefs,
		})

		const leafsOverridesMaps = getLeafsOverridesMapsRefs(templateComponent.id, queryName as 'layoutQuery')
		const parentRefCompResolvedPresets = getParentRefCompResolvedPresets()

		return {
			refArraysWithOverrides,
			leafsOverridesMaps,
			parentRefCompResolvedPresets,
		}
	},
	updateDependencies: ({ update, inflatedComponent, dependencies }) => {
		update(RESOLVED_REFS_NAMESPACE, LAYOUT_INFLATED_MAP, inflatedComponent.layoutQuery, dependencies, {
			schema: dependencies,
			removed: EMPTY_REMOVED,
		})
	},
	toViewItem: ({ id, value, rawModel }) => {
		const { refArraysWithOverrides } = value
		if (_.last(refArraysWithOverrides)!.type !== 'RefArray') {
			return { [id]: resolveOverrideItem(refArraysWithOverrides, id) }
		}

		const pageVariants = _.get(rawModel, [SITE_NAMESPACE, pointers.data.DATA_MAPS.VARIANTS], {})
		const inflatedDataItemsMap = getInflatedDataItemsMap(
			value,
			id,
			pageVariants,
			PRESET_RESOLVING_DATA_ITEM_TYPE[pointers.data.DATA_MAPS.LAYOUT]
		)

		const context = getContextFromInflatedId(id)
		const inflatedWidgetRootCompId = _.get(rawModel, [RESOLVED_REFS_NAMESPACE, context])

		return _.mapValues(inflatedDataItemsMap, (dataItem) => {
			if (dataItem.type === 'SingleLayoutData' && dataItem.variableConnections) {
				const variableConnections = dataItem.variableConnections.map((connection) => {
					const variableId = `${inflatedWidgetRootCompId}-${connection.variableId}`

					return {
						...connection,
						variableId,
					}
				})

				return {
					...dataItem,
					variableConnections,
				}
			}

			return dataItem
		})
	},
})
